// src/components/SessionMergePane.jsx
// 回をまとめる（出席記録の統合）画面【2026-09-28】
//
//   1回の授業が二度押し・開き直しなどで2つ以上の回に分かれたときに、1つに寄せる。
//   /classes/:classId/merge（?keep=回のID で残す回を選んだ状態で開く）
//
//   表は出席記録の「回ごとのサマリ・授業メモ」と同じ並び。行をクリックで選択／解除し、
//   選んだ中の1つを「残す回」にする（最初は出席の多い回）。同じ日の回だけまとめられる。
//   【2026-09-28 表示を回ごとのサマリと同じにした】
//   サーバー: POST /api/class-summary/sessions/merge（dryRun で下見。classSummaryController）
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmDialog } from './ConfirmDialog';

const auth = () => ({
  Authorization: 'Bearer ' + localStorage.getItem('token'),
  'Content-Type': 'application/json',
});

/** 授業日を 'YYYY-MM-DD'（端末の日付）にする。DATE は UTC の ISO で届くので先頭を切らない */
const fmtDate = (d) => {
  if (!d) return '-';
  const str = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const x = new Date(str);
  if (isNaN(x.getTime())) return str.slice(0, 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
};
const fmtClock = (v) => {
  if (!v) return '';
  const x = new Date(v);
  if (isNaN(x.getTime())) return '';
  return String(x.getHours()).padStart(2, '0') + ':' + String(x.getMinutes()).padStart(2, '0');
};
const STATUS_LABEL = { active: '進行中', completed: '終了', cancelled: '休講' };
const STATUS_COLOR = { active: '#2563eb', completed: '#4b5563', cancelled: '#6b7280' };
function rateColor(rate) {
  if (rate == null) return '#d1d5db';
  if (rate >= 80) return '#16a34a';
  if (rate >= 60) return '#d97706';
  return '#dc2626';
}

const CONFLICT = [
  { key: 'keep',   label: '残す回の記録を使う', note: '例: 残す回で遅刻・まとめる回で出席 → 遅刻' },
  { key: 'better', label: '良い方を使う',       note: '出席＞遅刻＞早退＞欠席。どちらかで出席なら出席' },
  { key: 'worse',  label: '悪い方を使う',       note: 'どちらかで遅刻なら遅刻、どちらかで欠席なら欠席' },
];
const SINGLE = [
  { key: 'move', label: '残す回へ移す',             note: 'どれかの回に記録があれば、その記録が残る' },
  { key: 'drop', label: '移さない',                 note: 'まとめる回にしか無い記録は捨てる。残す回の記録はそのまま' },
  { key: 'both', label: '全部の回にいた学生だけ残す', note: 'どれか1つでも記録が無い学生は、残す回の記録も消す（欠席扱い）' },
];

export default function SessionMergePane({ classId, className, setErr, setMsg }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData]         = useState(null);   // /api/class-summary/:classId の結果
  const firstKeep = Number(params.get('keep')) || null;
  const [picked, setPicked]     = useState(firstKeep ? [firstKeep] : []);
  const [keepId, setKeepId]     = useState(firstKeep);
  const [conflict, setConflict] = useState('keep');
  const [single, setSingle]     = useState('move');
  const [preview, setPreview]   = useState(null);
  const [busy, setBusy]         = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch('/api/class-summary/' + classId, { headers: auth() });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || d.message || '回を読み込めませんでした');
        if (alive) setData(d);
      } catch (e) { if (alive) { setErr(e.message); setData({ sessions: [] }); } }
    })();
    return () => { alive = false; };
  }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps

  // data が変わったときだけ作り直す（毎回新しい配列になると、下の useMemo が毎回計算し直しになる）
  const sessions = useMemo(() => (data && data.sessions) || [], [data]);
  const enrolled = data && data.overview ? Number(data.overview.enrolled_students) : 0;
  const rows = useMemo(() => sessions.filter(s => picked.includes(s.id)), [sessions, picked]);

  // 残す回: 選んだ中から。指定が無い・外れたら、受付中の回 → 出席の多い回の順に決める
  const keep = useMemo(() => {
    if (rows.length === 0) return null;
    const k = rows.find(s => s.id === keepId);
    if (k) return k;
    return rows.find(s => s.status === 'active')
      || [...rows].sort((a, b) => Number(b.attended_count) - Number(a.attended_count))[0];
  }, [rows, keepId]);
  const froms = rows.filter(s => keep && s.id !== keep.id);

  // 選べるか・まとめられるか
  const blocked = rows.length < 2 ? 'まとめる回を2つ以上選んでください'
    : new Set(rows.map(s => fmtDate(s.session_date))).size > 1 ? '日付が違う回はまとめられません（同じ日の回だけです）'
    : froms.some(s => s.status === 'active') ? '受付中の回は「残す回」にしか選べません'
    : null;

  const toggle = (s) => {
    if (s.status === 'cancelled') return;
    setPicked(v => (v.includes(s.id) ? v.filter(x => x !== s.id) : [...v, s.id]));
    if (keepId === s.id) setKeepId(null);
  };

  const body = (dryRun) => JSON.stringify({
    intoSessionId: keep && keep.id, fromSessionIds: froms.map(s => s.id), conflict, single, dryRun,
  });

  // 下見（サーバーで実際に流して巻き戻す）
  useEffect(() => {
    setPreview(null);
    if (blocked || !keep) return undefined;
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch('/api/class-summary/sessions/merge', { method: 'POST', headers: auth(), body: body(true) });
        const d = await r.json();
        if (alive) setPreview(d.success ? d : { error: d.message || 'まとめられません' });
      } catch (e) { if (alive) setPreview(null); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [picked, keep && keep.id, conflict, single, blocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const back = () => navigate('/classes/' + classId + '/summary');

  const run = async () => {
    if (blocked || !keep) return;
    const lines = froms.map(s => `・${fmtDate(s.session_date)} ${fmtClock(s.start_time)}（出席 ${s.attended_count}）`).join('\n');
    const c = CONFLICT.find(x => x.key === conflict), g = SINGLE.find(x => x.key === single);
    if (!(await confirmDialog(
      `「${fmtDate(keep.session_date)} ${fmtClock(keep.start_time)}（出席 ${keep.attended_count}）」に次の回をまとめます。\n\n${lines}\n\n`
      + `両方に記録がある学生: ${c.label}\n片方にしか記録がない学生: ${g.label}`
      + (preview && !preview.error ? `\n\nまとめたあとの出席: ${preview.attended} 人` : '')
      + '\n\nまとめた回は消えます。元には戻せません。\n\nまとめますか？'))) return;
    setBusy(true);
    try {
      const r = await fetch('/api/class-summary/sessions/merge', { method: 'POST', headers: auth(), body: body(false) });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || 'まとめられませんでした');
      setErr(''); setMsg(d.message);
      back();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (data === null) return <div className="card"><p>読み込み中...</p></div>;

  const step = { fontWeight: 700, color: '#1e3a8a', margin: '0 0 0.4rem' };
  const opt = (on) => ({
    display: 'block', border: `1px solid ${on ? '#2563eb' : '#e5e7eb'}`, background: on ? '#eff6ff' : '#fff',
    borderRadius: 8, padding: '0.5rem 0.7rem', marginBottom: '0.35rem', cursor: 'pointer',
  });

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <h2 style={{ margin: 0, flex: 1 }}>回をまとめる{className ? `（${className}）` : ''}</h2>
        <button className="btn-secondary" onClick={back}>← 出席記録へ戻る</button>
      </div>
      <p style={{ fontSize: '0.88rem', color: '#4b5563', marginTop: 0 }}>
        まとめたい回の行をクリックして選びます（もう一度クリックで解除）。選んだ中の1つが「残す回」です。
        まとめた回は消え、回数が減るので出席率も計算し直します。同じ日の回だけまとめられます。
      </p>

      {/* 回の一覧（回ごとのサマリと同じ並び） */}
      {sessions.length === 0 ? (
        <p style={{ color: '#6b7280' }}>まだ授業を実施していません。</p>
      ) : (
        <div className="table-wrap" style={{ marginBottom: '0.8rem' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>選択</th>
                <th style={{ whiteSpace: 'nowrap' }}>日付</th>
                <th style={{ whiteSpace: 'nowrap' }}>状態</th>
                <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>出席</th>
                <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>遅刻</th>
                <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>早退</th>
                <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>欠席</th>
                <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                    title="打刻はあるが、履修者名簿に載っていない学生の数">名簿未登録</th>
                <th style={{ minWidth: 120 }}>出席率</th>
                <th>メモ</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const cancelled = s.status === 'cancelled';
                const on        = picked.includes(s.id);
                const isKeep    = on && keep && keep.id === s.id;
                const attended  = Number(s.attended_count);
                const absent    = (!cancelled && enrolled > 0) ? Math.max(enrolled - attended, 0) : null;
                const sRate     = (!cancelled && enrolled > 0) ? Math.round(attended * 1000 / enrolled) / 10 : null;
                return (
                  <tr key={s.id} onClick={() => toggle(s)}
                      title={cancelled ? '休講の回はまとめられません' : (on ? 'クリックで選択を解除' : 'クリックで選択')}
                      style={{
                        cursor: cancelled ? 'not-allowed' : 'pointer',
                        color: cancelled ? '#9ca3af' : undefined,
                        background: isKeep ? '#dbeafe' : on ? '#eff6ff' : undefined,
                        boxShadow: on ? 'inset 4px 0 0 #2563eb' : undefined,
                      }}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {isKeep ? (
                        <span style={{ background: '#2563eb', color: '#fff', borderRadius: 999,
                                       padding: '0.1rem 0.55rem', fontSize: '0.78rem', fontWeight: 700 }}>残す</span>
                      ) : on ? (
                        <span style={{ display: 'inline-flex', gap: '0.3rem', alignItems: 'center' }}>
                          <span style={{ color: '#2563eb', fontSize: '0.8rem', fontWeight: 700 }}>まとめる</span>
                          <button type="button" className="btn-secondary"
                                  style={{ padding: '0.1rem 0.45rem', fontSize: '0.75rem' }}
                                  onClick={e => { e.stopPropagation(); setKeepId(s.id); }}
                                  title="この回を残す回にする">残す回にする</button>
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{cancelled ? '—' : '未選択'}</span>
                      )}
                    </td>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {fmtDate(s.session_date)}
                      {fmtClock(s.start_time) && (
                        <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '0.35rem', fontSize: '0.82rem' }}>
                          {fmtClock(s.start_time)}
                        </span>
                      )}
                    </td>
                    <td style={{ color: STATUS_COLOR[s.status] || '#4b5563', whiteSpace: 'nowrap' }}>
                      {STATUS_LABEL[s.status] || s.status}
                    </td>
                    <td style={{ textAlign: 'right' }}>{cancelled ? '—' : attended}</td>
                    <td style={{ textAlign: 'right' }}>{cancelled ? '—' : s.late_count}</td>
                    <td style={{ textAlign: 'right' }}>{cancelled ? '—' : s.early_leave_count}</td>
                    <td style={{ textAlign: 'right' }}>{absent == null ? '—' : absent}</td>
                    <td style={{ textAlign: 'right', color: Number(s.unenrolled_count) > 0 ? '#9a3412' : undefined }}>
                      {cancelled ? '—' : (Number(s.unenrolled_count) || 0)}
                    </td>
                    <td>
                      {sRate == null ? <span style={{ color: '#6b7280' }}>—</span> : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div className="progress-bar" style={{ flex: 1, minWidth: 54 }}>
                            <div className="progress-fill" style={{ width: sRate + '%', background: rateColor(sRate) }} />
                          </div>
                          <span style={{ fontSize: '0.8rem', color: rateColor(sRate), minWidth: 42, textAlign: 'right' }}>{sRate}%</span>
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.85rem', color: s.lesson_notes ? '#374151' : '#9ca3af',
                                 maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.lesson_notes ? s.lesson_notes.replace(/\s+/g, ' ') : '（メモなし）'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 選択の状態 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                    fontSize: '0.88rem', marginBottom: '0.9rem' }}>
        <strong>{rows.length} 回を選択中</strong>
        {keep && <span>残す回: {fmtDate(keep.session_date)} {fmtClock(keep.start_time)}（出席 {keep.attended_count}）</span>}
        {rows.length > 0 && (
          <button type="button" className="btn-secondary" style={{ padding: '0.15rem 0.6rem', fontSize: '0.8rem' }}
                  onClick={() => { setPicked([]); setKeepId(null); }}>選択をすべて解除</button>
        )}
        {blocked && rows.length > 0 && <span style={{ color: '#b45309' }}>{blocked}</span>}
      </div>

      {/* 扱い */}
      {!blocked && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div style={{ flex: '1 1 280px' }}>
            <p style={step}>両方に記録がある学生</p>
            {CONFLICT.map(o => (
              <label key={o.key} style={opt(conflict === o.key)}>
                <input type="radio" name="conflict" checked={conflict === o.key}
                       onChange={() => setConflict(o.key)} style={{ marginRight: '0.4rem' }} />
                <strong>{o.label}</strong>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginLeft: '1.4rem' }}>{o.note}</div>
              </label>
            ))}
          </div>
          <div style={{ flex: '1 1 280px' }}>
            <p style={step}>片方にしか記録がない学生</p>
            {SINGLE.map(o => (
              <label key={o.key} style={opt(single === o.key)}>
                <input type="radio" name="single" checked={single === o.key}
                       onChange={() => setSingle(o.key)} style={{ marginRight: '0.4rem' }} />
                <strong>{o.label}</strong>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginLeft: '1.4rem' }}>{o.note}</div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* 下見と実行 */}
      {!blocked && (
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.8rem',
                      display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240, fontSize: '0.9rem',
                        color: preview && preview.error ? '#b45309' : '#1e3a8a' }}>
            {!preview ? '下見を計算しています…'
              : preview.error ? preview.error
              : (<>
                  まとめたあと: 出席 <strong style={{ fontSize: '1.1rem' }}>{preview.attended}</strong> 人
                  <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                    移す {preview.moved}・重なり {preview.dropped}
                    {preview.changed ? `（うち書き換え ${preview.changed}）` : ''}
                    {preview.discarded ? `・捨てる ${preview.discarded}` : ''}
                    {preview.notInAll ? `・全部の回にいないため消す ${preview.notInAll} 人` : ''}
                  </div>
                </>)}
          </div>
          <button className="btn-primary" disabled={busy || !preview || !!preview.error} onClick={run}
                  style={{ minHeight: '2.4rem' }}>
            {busy ? 'まとめています…' : `${rows.length}つの回を1つにまとめる`}
          </button>
        </div>
      )}
    </div>
  );
}
